// The seam between this layer and however SSM actually gets called.
//
// One method. Two live implementations are possible and this file commits to
// neither, because that decision is still open:
//   (a) invoke the partner-owned Lambdas (separate repo + CDK stack), mapping
//       their response into PmsTransportResult;
//   (b) import @ssm/client directly and map its SsmResult.
// Nothing above this interface changes either way.
//
// Phase 1 only ever constructs the shadow transport, which contains no I/O
// code at all — that is what makes "zero vendor writes" structural rather than
// a matter of discipline.

import type { PmsLogger, PmsRequestEnvelope } from './types';

export type PmsTransportResult =
  | { kind: 'ok'; httpStatus: number; body: unknown }
  | { kind: 'http'; httpStatus: number; body: unknown }
  | { kind: 'network'; message: string; raw?: unknown }
  /** Built and inspected, deliberately not sent. */
  | { kind: 'shadow' };

export interface PmsTransport {
  call(request: PmsRequestEnvelope): Promise<PmsTransportResult>;
}

/**
 * Records what we would have sent and returns without sending it. There is no
 * fetch, no SDK and no URL in this function — by construction it cannot reach
 * a vendor.
 */
export function createShadowTransport(logger: PmsLogger): PmsTransport {
  return {
    call(request: PmsRequestEnvelope): Promise<PmsTransportResult> {
      logger.info(`[pms:shadow] would call ${request.vendorOp}`, request.body);
      return Promise.resolve({ kind: 'shadow' });
    },
  };
}

/** Canned responses keyed by vendorOp, for driving every error class in tests. */
export function createMockTransport(
  fixtures: Record<string, PmsTransportResult>,
): PmsTransport {
  return {
    call(request: PmsRequestEnvelope): Promise<PmsTransportResult> {
      const fixture = fixtures[request.vendorOp];
      if (!fixture) {
        return Promise.resolve({
          kind: 'network',
          message: `mock transport has no fixture for ${request.vendorOp}`,
        });
      }
      return Promise.resolve(fixture);
    },
  };
}
